<template>
  <div class="c-overview">
    <LevelSelect v-if="hasLevels" />

    <div class="c-overview__wrapper c-overview__wrapper--default">
      <VHeading header="h1" class="c-overview__title u-my">
        {{ $t('OVERVIEW_THEMES_OVERVIEW')
        }}<span v-if="isTeacher">, {{ userdata.name.givenName }}</span
        >!
      </VHeading>

      <div class="c-overview__inner">
        <div
          v-if="overviewTitle"
          class="c-overview__alerts"
          v-md-html="overviewTitle"
        />

        <div v-if="isTeacher" class="c-overview__teacher-units">
          <div class="c-overview__header" v-md-html="overviewTitle" />

          <div v-if="teacherUnits.length" class="c-overview__unit-item-wrapper">
            <div
              v-for="unit in teacherUnits"
              :key="unit.id"
              class="c-overview__unit-item"
            >
              <FHButton
                :href="`unit/${unit.slug}`"
                class="c-overview__teacher-unit"
                :tabindex="inactiveOverlay ? '-1' : '0'"
              >
                <img
                  v-if="
                    unit.cover &&
                      unit.cover.image_type &&
                      addCloudinaryUrlParams(
                        unit.cover.image_type.file,
                        'w_32,c_scale',
                      )
                  "
                  class="c-overview__teacher-unit-img"
                  :src="unit.cover.image_type.file.url"
                />
                {{ unit.name }}
              </FHButton>
            </div>
          </div>
        </div>

        <OverlayInactive v-if="inactiveOverlay" :text="inactiveOverlay" />

        <div
          v-if="!inactiveOverlay && overviewList.length"
          class="c-overview__units"
        >
          <div
            v-for="(parent, i) in overviewList"
            class="c-overview__units-item"
            :class="{ 'c-overview__units-item--grouped': parent.units.length }"
            :key="parent._id"
          >
            <div v-if="parent.units.length" class="c-overview__parent-group">
              <VHeading header="h2" class="c-overview__parent-title">
                {{ parent.name }}
              </VHeading>
              <UnitTile
                v-for="(unit, j) in parent.units"
                class="c-overview__parent-item u-mr u-mb"
                :class="[
                  `c-unit-tile--${unit.slug}`,
                  {
                    inactive:
                      !unit.cover || !unit.cover.small || inactiveOverlay,
                  },
                ]"
                :tabindex="
                  unit.cover && unit.cover.small && !inactiveOverlay
                    ? '0'
                    : '-1'
                "
                :key="unit._id"
                :title="unit.name"
                :index="j"
                :slug="unit.slug"
                :cover="unit.cover && unit.cover.small"
              />
            </div>
            <UnitTile
              v-else
              class="c-overview__parent-item u-mr u-mb"
              :class="[
                `c-unit-tile--${parent.slug}`,
                { inactive: !parent.cover || inactiveOverlay },
              ]"
              :tabindex="
                parent.cover && parent.cover.small && !inactiveOverlay
                  ? '0'
                  : '-1'
              "
              :key="parent._id"
              :title="parent.name"
              :index="i"
              :slug="parent.slug"
              :cover="parent.cover && parent.cover.small"
            >
            </UnitTile>
          </div>
        </div>
        <ContentLoader v-if="loading">
          <rect y="0" x="1rem" width="21%" height="4rem"></rect>
          <rect y="0" x="27%" width="21%" height="4rem"></rect>
          <rect y="0" x="50%" width="21%" height="4rem"></rect>
          <rect y="0" x="73%" width="21%" height="4rem"></rect>
          <rect y="4.5rem" x="1rem" width="21%" height="4rem"></rect>
          <rect y="4.5rem" x="27%" width="21%" height="4rem"></rect>
          <rect y="4.5rem" x="50%" width="21%" height="4rem"></rect>
          <rect y="4.5rem" x="73%" width="21%" height="4rem"></rect>
        </ContentLoader>

        <div
          v-if="!inactiveOverlay && extraContent"
          class="c-overview__units c-overview__units--extra-content u-px u-px--3"
        >
          <VHeading header="h2" class="c-overview__parent-title">
            {{ extraContent.title }}
          </VHeading>
          <UnitTile
            v-for="addon in extraContent.items"
            class="c-overview__parent-item u-mr u-mb"
            :class="[{ inactive: inactiveOverlay }]"
            :key="addon.title"
            :title="addon.title"
            :slug="addon.url"
            :cover="addon.cover"
            :tabindex="!inactiveOverlay ? '0' : '-1'"
            :target="addon.target || '_blank'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { ContentLoader } from 'vue-content-loader'
import VHeading from '@forlagshuset/v-heading'
import FHButton from '@forlagshuset/v-button'
import UnitTile from '@/components/UnitTile'
import LevelSelect from '@/components/LevelSelect'
import OverlayInactive from '@/components/OverlayInactive'

import { addCloudinaryUrlParams, mergeUnits } from '@/utils'

export default {
  name: 'OverviewPrimary',

  components: {
    VHeading,
    FHButton,
    UnitTile,
    ContentLoader,
    LevelSelect,
    OverlayInactive,
  },

  setup(props, ctx) {
    const loading = ref(true)
    const overviewTitle = ref('')
    const overviewList = ref([])
    const teacherUnits = ref([])
    const teacherUnitLessons = ref([])
    const userdata = computed(() => ctx.root.$store.state.user.user)
    const isTeacher = computed(
      () => ctx.root.$store.getters['appConfig/isTeacher'],
    )
    const hasLevels = computed(
      () => ctx.root.$store.getters['appConfig/hasLevels'],
    )
    const extraContent = computed(() => {
      const addons = ctx.root.$store.state.appConfig.addons

      if (addons && addons.overview && addons.overview.extra) {
        return addons.overview.extra
      }

      return null
    })
    const teacherExtraContent = computed(() => {
      const teacherAddons = ctx.root.$store.state.appConfig.teacherAddons

      if (
        teacherAddons &&
        teacherAddons.overview &&
        teacherAddons.overview.extra
      ) {
        return teacherAddons.overview.extra
      }

      return null
    })
    const inactiveOverlay = computed(
      () => ctx.root.$store.getters['appConfig/getInactiveOverlay'],
    )

    onMounted(async () => {
      await ctx.root.$store.dispatch('content/setCourse')
      await ctx.root.$store.dispatch('content/setUnits')
      const course = ctx.root.$store.getters['content/getCourse']
      overviewTitle.value = course.description || ''
      const units = ctx.root.$store.getters['content/getUnits']

      units.map(unit => {
        if (unit.cover && unit.cover.image_type) {
          const coverURL = unit.cover.image_type.file.url
          const param = 'h_192,c_scale' // Should be adjusted per project need (styling overview list for example)
          unit.cover.small = addCloudinaryUrlParams(coverURL, param)
        }

        return unit
      })

      teacherUnits.value = units.filter(unit => unit.teacherUnit)
      //gather parents
      overviewList.value = mergeUnits(units.filter(unit => !unit.teacherUnit))
      loading.value = false
    })

    return {
      loading,
      userdata,
      overviewTitle,
      overviewList,
      isTeacher,
      teacherUnits,
      hasLevels,
      extraContent,
      teacherExtraContent,
      teacherUnitLessons,
      inactiveOverlay,
    }
  },
}
</script>
